/**
 * Table — the presentational primitive for simple hand-rolled tables
 * (design-system-lld.md §4.8). Styled sub-elements rather than a
 * columns/rows config API, because existing tables have bespoke cells and
 * rowspans that would force escape hatches immediately.
 *
 * `DataTable` (server-side search, column configuration) is kept as-is and
 * stays fenced to the leads console — this component is for the ~51 files
 * hand-rolling a plain `<table>`.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Wraps in the canonical card surface. Set false when the caller already supplies one (e.g. inside CardBody padding="none"). */
  bare?: boolean;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(({ className, bare = false, ...props }, ref) => {
  const table = <table ref={ref} className={cn('w-full text-left text-sm', className)} {...props} />;
  if (bare) return table;
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      {table}
    </div>
  );
});
Table.displayName = 'Table';

const THead = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500', className)} {...props} />
  ),
);
THead.displayName = 'THead';

const TBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={cn('divide-y divide-gray-100', className)} {...props} />,
);
TBody.displayName = 'TBody';

export interface TRProps extends React.HTMLAttributes<HTMLTableRowElement> {
  clickable?: boolean;
}

const TR = React.forwardRef<HTMLTableRowElement, TRProps>(({ className, clickable = false, ...props }, ref) => (
  <tr ref={ref} className={cn(clickable && 'cursor-pointer hover:bg-gray-50', className)} {...props} />
));
TR.displayName = 'TR';

export interface CellAlignProps {
  align?: 'left' | 'right' | 'center';
}

const ALIGN_CLASSES: Record<NonNullable<CellAlignProps['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

const TH = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement> & CellAlignProps>(
  ({ className, align = 'left', ...props }, ref) => (
    <th ref={ref} className={cn('px-4 py-3 font-semibold', ALIGN_CLASSES[align], className)} {...props} />
  ),
);
TH.displayName = 'TH';

const TD = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement> & CellAlignProps>(
  ({ className, align = 'left', ...props }, ref) => (
    <td ref={ref} className={cn('px-4 py-3 text-sm text-gray-700', ALIGN_CLASSES[align], className)} {...props} />
  ),
);
TD.displayName = 'TD';

export { Table, THead, TBody, TR, TH, TD };
